import { useState } from "react";
import { t } from "ttag";

import { getErrorMessage } from "metabase/api/utils/errors";
import { ConfirmModal } from "metabase/common/components/ConfirmModal";
import { ExternalLink } from "metabase/common/components/ExternalLink";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { dayjs } from "metabase/dayjs";
import {
  Alert,
  Badge,
  Button,
  Group,
  Icon,
  List,
  Modal,
  Progress,
  Stack,
  Text,
  Textarea,
} from "metabase/ui";
import type {
  GitSyncStatus,
  GitSyncTask,
  GitSyncTaskOutcome,
} from "metabase-types/api";

import {
  useExportGitSyncMutation,
  useImportGitSyncMutation,
  useOpenGitSyncPullRequestMutation,
} from "../api";
import { useGitSyncStatus } from "../hooks";
import {
  MAX_LISTED_CONFLICTS,
  getRunningTaskMessage,
  getShortCommit,
  getTaskInitiatorName,
  getTaskOutcomeMessage,
  isTaskRunning,
} from "../utils";

/**
 * Where Git sync stands, with the controls to export, open a pull request and
 * import: the branch, the last export and import, the running task's
 * progress, and how the latest task ended.
 */
export function GitSyncStatusPanel() {
  const { status, currentTask, isLoading, error } = useGitSyncStatus();

  if (isLoading || error || !status) {
    return <LoadingAndErrorWrapper loading={isLoading} error={error} />;
  }

  return <GitSyncStatusContent status={status} currentTask={currentTask} />;
}

type GitSyncStatusContentProps = {
  status: GitSyncStatus;
  currentTask: GitSyncTask | null;
};

function GitSyncStatusContent({
  status,
  currentTask,
}: GitSyncStatusContentProps) {
  const [exportGitSync, exportState] = useExportGitSyncMutation();
  const [openPullRequest, pullRequestState] =
    useOpenGitSyncPullRequestMutation();
  const [importGitSync, importState] = useImportGitSyncMutation();
  const [messageModal, setMessageModal] = useState<
    "export" | "pull-request" | null
  >(null);
  const [confirmation, setConfirmation] = useState<
    "force-export" | "force-import" | null
  >(null);

  const isReadOnly = status.type === "read-only";
  const isRunning = isTaskRunning(currentTask);
  const isStarting =
    exportState.isLoading ||
    pullRequestState.isLoading ||
    importState.isLoading;
  const latestTask = status.latest_task;
  const startError =
    exportState.error ?? pullRequestState.error ?? importState.error;

  const handleExport = async (message: string, force = false) => {
    setMessageModal(null);
    await exportGitSync({ message: message || undefined, force });
  };

  const handleOpenPullRequest = async (message: string) => {
    setMessageModal(null);
    await openPullRequest({ message: message || undefined });
  };

  const handleImport = async (force = false) => {
    await importGitSync({ force });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap="xs">
          <Group gap="sm">
            <Icon name="git_branch" c="icon-secondary" />
            <Text fw="bold">{status.branch}</Text>
            <Badge variant="light" color={isReadOnly ? "warning" : "brand"}>
              {isReadOnly ? t`Read-only` : t`Read-write`}
            </Badge>
            {status.auto_import && (
              <Badge
                variant="light"
                color="neutral"
              >{t`Imports on a schedule`}</Badge>
            )}
          </Group>
          {status.url && (
            <Text c="text-secondary" fz="sm">
              {status.url}
            </Text>
          )}
          <Text c="text-secondary" fz="sm">
            {status.last_synced_commit
              ? t`Last synced commit: ${getShortCommit(status.last_synced_commit)}`
              : t`Not synced yet`}
          </Text>
          <LastTaskLine label={t`Last export`} task={status.last_export} />
          <LastTaskLine label={t`Last import`} task={status.last_import} />
        </Stack>
        <Group gap="sm" wrap="nowrap">
          {!isReadOnly && (
            <Button
              leftSection={<Icon name="upload" aria-hidden />}
              disabled={isRunning || isStarting}
              onClick={() => setMessageModal("export")}
            >
              {t`Export`}
            </Button>
          )}
          <Button
            leftSection={<Icon name="git_branch" aria-hidden />}
            variant="default"
            disabled={isRunning || isStarting}
            onClick={() => setMessageModal("pull-request")}
          >
            {t`Open pull request`}
          </Button>
          <Button
            leftSection={<Icon name="download" aria-hidden />}
            variant={isReadOnly ? "filled" : "default"}
            disabled={isRunning || isStarting}
            onClick={() => handleImport()}
          >
            {t`Import`}
          </Button>
        </Group>
      </Group>

      {currentTask && isRunning ? (
        <RunningTask task={currentTask} />
      ) : (
        latestTask && (
          <TaskOutcome
            task={latestTask}
            isReadOnly={isReadOnly}
            onForceExport={() => setConfirmation("force-export")}
            onForceImport={() => setConfirmation("force-import")}
            onImport={() => handleImport()}
          />
        )
      )}

      {startError != null && (
        <Alert color="error" icon={<Icon name="warning" />}>
          {getErrorMessage(startError)}
        </Alert>
      )}

      <CommitMessageModal
        opened={messageModal === "export"}
        title={t`Export to Git`}
        submitLabel={t`Export`}
        onClose={() => setMessageModal(null)}
        onSubmit={(message) => handleExport(message)}
      />
      <CommitMessageModal
        opened={messageModal === "pull-request"}
        title={t`Open a pull request`}
        description={t`This commits the synced content as it is here to a new branch and opens a pull request into ${status.branch}. Changes ${status.branch} has that aren't imported here show as undone in it. ${status.branch} only changes once the pull request is merged.`}
        submitLabel={t`Open pull request`}
        onClose={() => setMessageModal(null)}
        onSubmit={handleOpenPullRequest}
      />
      <ConfirmModal
        opened={confirmation === "force-export"}
        title={t`Export anyway?`}
        message={t`The export overwrites the changes on the branch that aren't imported here.`}
        confirmButtonText={t`Export anyway`}
        confirmButtonProps={{ color: "negative" }}
        onConfirm={async () => {
          setConfirmation(null);
          await handleExport("", true);
        }}
        onClose={() => setConfirmation(null)}
      />
      <ConfirmModal
        opened={confirmation === "force-import"}
        title={t`Discard local changes?`}
        message={t`The import replaces the synced content changed here since the last sync, and deletes what was created here and isn't on the branch.`}
        confirmButtonText={t`Discard and import`}
        confirmButtonProps={{ color: "negative" }}
        onConfirm={async () => {
          setConfirmation(null);
          await handleImport(true);
        }}
        onClose={() => setConfirmation(null)}
      />
    </Stack>
  );
}

function LastTaskLine({
  label,
  task,
}: {
  label: string;
  task: GitSyncTask | null;
}) {
  if (!task?.ended_at) {
    return null;
  }
  const when = dayjs(task.ended_at).fromNow();
  const who = getTaskInitiatorName(task.initiated_by_user);
  return (
    <Text c="text-secondary" fz="sm">
      {t`${label}: ${when} (${who})`}
    </Text>
  );
}

function RunningTask({ task }: { task: GitSyncTask }) {
  const percent = Math.round((task.progress ?? 0) * 100);
  return (
    <Stack gap="xs" aria-live="polite">
      <Text fw="bold">{getRunningTaskMessage(task)}</Text>
      <Progress value={percent} aria-label={getRunningTaskMessage(task)} />
    </Stack>
  );
}

type TaskOutcomeProps = {
  task: GitSyncTask;
  isReadOnly: boolean;
  onForceExport: () => void;
  onForceImport: () => void;
  onImport: () => void;
};

function TaskOutcome({
  task,
  isReadOnly,
  onForceExport,
  onForceImport,
  onImport,
}: TaskOutcomeProps) {
  const message = getTaskOutcomeMessage(task);
  const listedConflicts = task.conflicts.slice(0, MAX_LISTED_CONFLICTS);
  const hiddenConflictCount = task.conflicts.length - listedConflicts.length;

  if (task.status === "successful") {
    const unmatched = task.outcome?.unmatched ?? [];
    const listedUnmatched = unmatched.slice(0, MAX_LISTED_CONFLICTS);
    const hiddenUnmatchedCount = unmatched.length - listedUnmatched.length;
    return (
      <Alert
        color={unmatched.length > 0 ? "warning" : "success"}
        icon={<Icon name={unmatched.length > 0 ? "warning" : "check"} />}
      >
        <Stack gap="sm">
          <Text>{message}</Text>
          {task.outcome && <PullRequestDetails outcome={task.outcome} />}
          {listedUnmatched.length > 0 && (
            <>
              <Text>{t`Some of the data model couldn't be applied here:`}</Text>
              <List size="sm" aria-label={t`Not applied`}>
                {listedUnmatched.map((name) => (
                  <List.Item key={name}>{name}</List.Item>
                ))}
                {hiddenUnmatchedCount > 0 && (
                  <List.Item>{t`and ${hiddenUnmatchedCount} more`}</List.Item>
                )}
              </List>
            </>
          )}
        </Stack>
      </Alert>
    );
  }

  if (task.status === "conflict") {
    const isRemoteChanged = task.outcome?.kind === "remote-changed";
    return (
      <Alert color="warning" icon={<Icon name="warning" />}>
        <Stack gap="sm">
          <Text>{message}</Text>
          {listedConflicts.length > 0 && (
            <List size="sm">
              {listedConflicts.map((conflict) => (
                <List.Item key={conflict}>{conflict}</List.Item>
              ))}
              {hiddenConflictCount > 0 && (
                <List.Item>{t`and ${hiddenConflictCount} more`}</List.Item>
              )}
            </List>
          )}
          <Group gap="sm">
            {isRemoteChanged ? (
              <>
                <Button
                  size="compact-sm"
                  onClick={onImport}
                >{t`Import`}</Button>
                {!isReadOnly && (
                  <Button
                    size="compact-sm"
                    variant="default"
                    onClick={onForceExport}
                  >
                    {t`Export anyway`}
                  </Button>
                )}
              </>
            ) : (
              <Button
                size="compact-sm"
                variant="default"
                onClick={onForceImport}
              >
                {t`Discard local changes and import`}
              </Button>
            )}
          </Group>
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert color="error" icon={<Icon name="warning" />}>
      {message}
    </Alert>
  );
}

function PullRequestDetails({ outcome }: { outcome: GitSyncTaskOutcome }) {
  if (outcome.kind !== "pull-request" && outcome.kind !== "branch-pushed") {
    return null;
  }
  const isOpened = outcome.kind === "pull-request";
  return (
    <>
      {outcome.detail && <Text>{outcome.detail}</Text>}
      {outcome.url && (
        <ExternalLink href={outcome.url}>
          {isOpened ? t`View the pull request` : t`Open the pull request`}
        </ExternalLink>
      )}
    </>
  );
}

type CommitMessageModalProps = {
  opened: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  onClose: () => void;
  onSubmit: (message: string) => void;
};

function CommitMessageModal({
  opened,
  title,
  description,
  submitLabel,
  onClose,
  onSubmit,
}: CommitMessageModalProps) {
  const [message, setMessage] = useState("");

  return (
    <Modal opened={opened} title={title} onClose={onClose}>
      <Stack gap="lg">
        {description && <Text>{description}</Text>}
        <Textarea
          label={t`Commit message`}
          placeholder={t`Describe what changed (optional)`}
          value={message}
          autosize
          minRows={3}
          onChange={(event) => setMessage(event.target.value)}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>{t`Cancel`}</Button>
          <Button
            variant="filled"
            onClick={() => {
              onSubmit(message.trim());
              setMessage("");
            }}
          >
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
